import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { RealtimeProvider } from "@/context/RealtimeContext";
import { Toaster } from "sonner";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Watchlists from "@/pages/Watchlists";
import WatchlistDetail from "@/pages/WatchlistDetail";
import Outliers from "@/pages/Outliers";
import Creators from "@/pages/Creators";
import CreatorDetail from "@/pages/CreatorDetail";
import VideoDetail from "@/pages/VideoDetail";
import ThreadPage from "@/pages/ThreadPage";
import Saved from "@/pages/Saved";
import Analytics from "@/pages/Analytics";
import Trends from "@/pages/Trends";
import Sources from "@/pages/Sources";
import HookRemix from "@/pages/HookRemix";
import Alerts from "@/pages/Alerts";
import Compare from "@/pages/Compare";
import Patterns from "@/pages/Patterns";
import SharedReport from "@/pages/SharedReport";
import Team from "@/pages/Team";
import Decisions from "@/pages/Decisions";
import Settings from "@/pages/Settings";
import Join from "@/pages/Join";

function Protected() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background text-muted-foreground">
        Loading...
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}

function PublicOnly() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    const pending = localStorage.getItem("op_pending_join");
    if (pending) return <Navigate to={`/join/${pending}`} replace />;
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <RealtimeProvider>
            <Toaster position="top-right" richColors />
            <Routes>
              <Route path="/share/:token" element={<SharedReport />} />
              <Route path="/join/:token" element={<Join />} />
              <Route element={<PublicOnly />}>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
              </Route>
              <Route element={<Protected />}>
                <Route path="/" element={<Dashboard />} />
                <Route path="/watchlists" element={<Watchlists />} />
                <Route path="/watchlists/:id" element={<WatchlistDetail />} />
                <Route path="/outliers" element={<Outliers />} />
                <Route path="/creators" element={<Creators />} />
                <Route path="/creators/:id" element={<CreatorDetail />} />
                <Route path="/videos/:id" element={<VideoDetail />} />
                <Route path="/threads/:videoId" element={<ThreadPage />} />
                <Route path="/saved" element={<Saved />} />
                <Route path="/analytics" element={<Analytics />} />
                <Route path="/trends" element={<Trends />} />
                <Route path="/sources" element={<Sources />} />
                <Route path="/hook-lab" element={<HookRemix />} />
                <Route path="/alerts" element={<Alerts />} />
                <Route path="/compare" element={<Compare />} />
                <Route path="/patterns" element={<Patterns />} />
                <Route path="/team" element={<Team />} />
                <Route path="/decisions" element={<Decisions />} />
                <Route path="/settings" element={<Settings />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </RealtimeProvider>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
